import { Alert, Button, Dropdown, Icon, Menu, Table } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import moment from 'moment';
import * as React from 'react';

import { ICurrency, SpecialTransactionIds } from 'src/apis/common';
import ConfirmationModal from "src/components/ConfirmationModal";
import BillingDateModal from 'src/components/Reporting/BillingDateModal';
import ChangePaymentMethodModal from "src/components/Reporting/ChangePaymentMethodModal";
import CreditsModal from 'src/components/Reporting/CreditsModal';
import GracePeriodModal from 'src/components/Reporting/GracePeriodModal';
import ManualChargeModal from "src/components/Reporting/ManualChargeModal";
import RefundModal from 'src/components/RefundModal';
import {
  getChargeHistory,
  IInvoice,
  ILineItem,
  IUsageDetail,
  resetBillingNotifications,
  retryTransaction,
  setGracePeriod,
  voidTransaction,
} from '../../apis/Reporting/ChargeHistory';
import '../../assets/css/chargeHistory.css';
import '../../assets/css/generalStyles.scss';
import { NavLink } from 'react-router-dom';
import deletePaymentInstrument from '../Companies/deletePaymentInstrument';
import SetStripeCustomerModal from './SetStripeCustomerModal';

export interface ILocalProps {
    companyID: number,
    token: string,
}

interface ILocalState {
    confirmationParams: any,
    creditAmount: string,
    creditBalance: string,
    currency?: ICurrency,
    // The highlighted invoice in the main table.
    currentSelectedInvoice?: IInvoiceTable,
    customer: any,
    errorMessage: string | null,
    expandedRowKeys: number[],
    gracePeriod: string | undefined,
    invoices: IInvoice[],
    loading: boolean,
    processorName: string,
    showBillingDateModal: boolean,
    showChangePaymentMethodModal: boolean,
    showConfirmationModal: boolean,
    showCreditsModal: boolean,
    showGracePeriodModal: boolean,
    showManualChargeModal: boolean,
    showRefundModal: boolean,
    showSetStripeCustomerModal: boolean,
    subscription: any,
    // The invoice we are currently operating on, if any.
    targetInvoice?: IInvoice;
}

interface IInvoiceTable {
    amountBilled: string,
    amountFromCard: string,
    amountFromCredit: string,
    key: number,
    note: string | null,
    statementDate: string,
    settlementDate: string,
    transactionID: string,
    isPendingAuth: boolean,
}

const isRefundable = (record: IInvoiceTable) => {
    return record.transactionID
        && record.transactionID !== SpecialTransactionIds.UNCOLLECTIBLE
        && record.transactionID !== SpecialTransactionIds.VOID
        && record.transactionID !== SpecialTransactionIds.ERROR;
}

const isRetryable = (record: IInvoiceTable) => {
    // Specifically we want to allow retrying failed manual charges.
    return !record.transactionID || record.transactionID === SpecialTransactionIds.UNCOLLECTIBLE;
}

const isVoidable = (record: IInvoiceTable) => {
    // Currently, we only can tell if it's already voided.
    if (record.transactionID && record.transactionID === SpecialTransactionIds.VOID) {
        return false;
    }
    // The backend will return an error if there are other properties that make it not voidable
    return true;
};

interface IInvoiceDetailTable {
    amount: string,
    childCompany: string | null,
    description: string | null,
    units: number,
}

interface IUsageDetailTable {
    key: string,
    property: string,
    value: string
}

interface IUsageDetailSFCProps {
    date: string,
    details: IUsageDetail[],
}

const UsageDetails: React.SFC<IUsageDetailSFCProps> = (props: IUsageDetailSFCProps): JSX.Element => {
    const getNameFromKey = (key: string): string => {
        switch (key) {
            case 'companyProfileID':
                return 'Company Profile ID';
            case 'pageImpressions':
                return 'Page Impressions';
            case 'leads':
                return 'Leads';
            case 'emails':
                return 'Emails';
            case 'clients':
                return 'Clients';
            case 'createTimestamp':
                return 'Create Timestamp';
            case 'updateTimestamp':
                return 'Update Timestamp';
            case 'hasCRM':
                return 'Salesforce';
            case 'litmusContent':
                return 'Litmus Tests';
            case 'shutterstock':
                return 'Shutterstock Images';
            case 'piesyncConns':
                return 'PieSync Connections';
            case 'mailboxes':
                return 'Synced Mailboxes';
            case 'activeUsers':
                return 'Active Users';
            case 'freeMailboxes':
                return 'Free Mailboxes';
            default:
                return key;
        }
    };

    const usageDetailData = (usageDetail: IUsageDetail) => {
        const hiddenFields = [
            'id',
            'customerID',
            'companyName',
            // The logo URL is too long, makes the table too wide.
            'companyLogoURL',
            'createTimestamp',
            'updateTimestamp'
        ];
        const usageDetailTableData: IUsageDetailTable[] = Object.keys(usageDetail)
            .filter(key => hiddenFields.indexOf(key) === -1)
            .map(key => {
                if (key === 'litmusContent') {
                    // @ts-ignore fix this later
                    const value = usageDetail[key] === null ? 'None' : 'See DB';
                    return ({
                        key: `${key}`,
                        property: getNameFromKey(key),
                        value: `${value}`,
                    });
                } else {
                    return ({
                        key: `${key}`,
                        property: getNameFromKey(key),
                        // @ts-ignore fix this later
                        value: `${usageDetail[key]}`,
                    });
                }

            });
        return usageDetailTableData;
    };

    const usageDetailColumns: Array<ColumnProps<IUsageDetailTable>> = [
        {
            align: 'left',
            dataIndex: 'property',
            width: '50%',
        },
        {
            align: 'right',
            dataIndex: 'value',
            width: '50%',
        }
    ];

    return (
        <div className='usageDetailContainer'>
            {props.details.map((usageDetail: IUsageDetail) => {
                const titleString = () => `${usageDetail.companyName}\nusage details for ${props.date}`;

                return (
                    <div className='usageDetail' key={usageDetail.id}>
                        <Table
                            columns={usageDetailColumns}
                            dataSource={usageDetailData(usageDetail)}
                            key={usageDetail.id}
                            pagination={false}
                            showHeader={false}
                            title={titleString}
                            size='small'
                        />
                    </div>
                );
            })}
        </div>
    );

};

class ChargeHistory extends React.Component<ILocalProps, ILocalState> {
    public constructor(props: ILocalProps) {
        super(props);
        this.state = {
            confirmationParams: {},
            creditBalance: '',
            creditAmount: '',
            customer: {},
            errorMessage: null,
            expandedRowKeys: [],
            gracePeriod: undefined,
            invoices: [],
            loading: true,
            processorName: 'Unknown',
            showBillingDateModal: false,
            showChangePaymentMethodModal: false,
            showConfirmationModal: false,
            showCreditsModal: false,
            showGracePeriodModal: false,
            showManualChargeModal: false,
            showRefundModal: false,
            showSetStripeCustomerModal: false,
            subscription: {},
        };
    }

    public async componentDidMount() {
        await this.getChargeHistory();
    }

    public render() {
        const invoiceColumns: Array<ColumnProps<IInvoiceTable>> = [
            {
                align: 'left',
                className: 'display-linebreaks',
                dataIndex: 'statementDate',
                title: 'Statement\nDate',
                width: '10%',
            },
            {
                align: 'left',
                className: 'display-linebreaks',
                dataIndex: 'settlementDate',
                title: 'Settlement\nDate',
                width: '10%',
            },
            {
                align: 'right',
                className: 'display-linebreaks',
                dataIndex: 'amountBilled',
                title: 'Amount\nBilled',
                width: '10%',
            },
            {
                align: 'right',
                className: 'display-linebreaks',
                dataIndex: 'amountFromCard',
                title: 'Amount\nFrom Card',
                width: '10%',
            },
            {
                align: 'right',
                className: 'display-linebreaks',
                dataIndex: 'amountFromCredit',
                title: 'Amount\nFrom Credit',
                width: '10%',
            },
            {
                align: 'left',
                render: (text: string, record) => (record.transactionID + (record.isPendingAuth ? ' (Pending Auth)' : '')),
                title: 'Transaction ID',
                width: '20%',
            },
            {
                align: 'left',
                dataIndex: 'note',
                title: 'Notes',
                width: '30%',
            },
            {
                align: 'right',
                render: (text: string, record) => {
                    return (
                        <Dropdown
                          placement="bottomLeft"
                          overlay={
                            <Menu>
                                {isRefundable(record) &&
                                <Menu.Item>
                                    <div onClick={this.openRefundModal.bind(this, record.key)}>Refund Transaction</div>
                                </Menu.Item>
                                }
                                {isRetryable(record) &&
                                <Menu.Item>
                                    <div onClick={this.openRetryModal(record.key)}>Retry Transaction</div>
                                </Menu.Item>
                                }
                                {isVoidable(record) &&
                                <Menu.Item>
                                    <div onClick={this.openVoidModal(record.key)}>Void Transaction</div>
                                </Menu.Item>
                                }
                            </Menu>
                        }>
                            <Button type={'link'}>Select<Icon type={'caret-down'} /></Button>
                        </Dropdown>

                    );
                },
                title: 'Actions',
                width: '13%',
            },
        ];

        const invoiceTableData: IInvoiceTable[] = this.state.invoices.map((invoice: IInvoice) => {
            return {
                amountBilled: invoice.amountBilled,
                amountFromCard: invoice.amountFromCard,
                amountFromCredit: invoice.amountFromCredit,
                statementDate: moment(invoice.statementDate).format('MMMM D, YYYY'),
                settlementDate: (invoice.settlementDate && invoice.settlementDate !== '0000-00-00 00:00:00') ?
                    moment(invoice.settlementDate).format('MMMM D, YYYY') : '',
                key: invoice.id,
                note: invoice.note,
                transactionID: invoice.transactionID,
                isPendingAuth: invoice.isPendingAuth,
            }
        });

        const moreOptions = (
            <Dropdown overlay={
                <Menu>
                    { this.state.subscription.planType === "Marketing Automation Reseller" &&
                    <Menu.Item>
                        <NavLink to={`/aclManagement/${this.state.customer.companyProfileID}`}>ACL Management</NavLink>
                    </Menu.Item>
                    }
                    <Menu.Item>
                        <div onClick={this.openCreditsModal}>Manage Credits For {`${this.state.customer.customerName}`}</div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={this.openManualChargeModal}>Manually Charge {`${this.state.customer.customerName}`}</div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={this.openBillingDateModal}>Change Billing Date</div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={this.openChangePaymentMethodModal}>Change Payment Method</div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={this.openSetStripeCustomerModal}>Set Stripe Customer</div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={() => deletePaymentInstrument(this.state.customer.companyProfileID, this.state.customer.customerName)}>
                            Clear Stored Payment Method
                        </div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={this.openResetNotificationModal}>Reset Billing Notifications</div>
                    </Menu.Item>
                    <Menu.Item>
                        <div onClick={this.openGracePeriodModal}>Grace Period</div>
                    </Menu.Item>
                </Menu>
            }>
                <div className="settings-icon">
                    <Icon className="setting c-pointer" type="setting" />
                </div>
            </Dropdown>
        );

        return (
            <div>
                {this.state.showBillingDateModal &&
                    <BillingDateModal
                        currentDay={this.state.customer.cycleDay}
                        customerID={this.state.customer.id}
                        customerName={this.state.customer.customerName}
                        handleClose={this.closeBillingDateModal}
                        handleSubmit={this.submitBillingDateModal}
                    />
                }
                {this.state.showChangePaymentMethodModal &&
                    <ChangePaymentMethodModal
                        companyProfileID={this.state.customer.companyProfileID}
                        currentPaymentMethod={this.state.customer.paymentMethodID}
                        customerName={this.state.customer.customerName}
                        handleClose={this.closeChangePaymentMethodModal}
                        handleSubmit={this.submitChangePaymentMethodModal}
                    />
                }
                {this.state.showSetStripeCustomerModal &&
                    <SetStripeCustomerModal
                        companyProfileID={this.state.customer.companyProfileID}
                        customerName={this.state.customer.customerName}
                        handleClose={this.closeSetStripeCustomerModal}
                        handleSubmit={this.submitSetStripeCustomerModal}
                    />
                }
                {this.state.showCreditsModal &&
                    <CreditsModal
                        companyID={this.props.companyID}
                        companyName={this.state.customer.customerName}
                        token={this.props.token}
                        handleSubmit={this.closeCreditsModal}
                    />
                }
                {this.state.showRefundModal &&
                    <RefundModal
                        customerID={this.state.customer.id}
                        companyName={this.state.customer.customerName}
                        // If we're showing the refund modal, we've should have a targetInvoice.
                        currency={this.state.targetInvoice!.currency}
                        invoice={this.state.targetInvoice!}
                        requestClose={this.closeRefundModal}
                    />
                }
                {this.state.showManualChargeModal &&
                <ManualChargeModal
                    currencySymbol={this.state.currency!.symbol}
                    customerID={this.state.customer.id}
                    customerName = {this.state.customer.customerName}
                    creditBalance={this.state.creditBalance}
                    gracePeriodActive={this.state.gracePeriod ? moment(this.state.gracePeriod).isAfter(moment()) : false}
                    handleClose={this.closeManualChargeModal}
                    handleSubmit={this.submitManualChargeModal}
                />
                }
                {this.state.showGracePeriodModal &&
                    <GracePeriodModal
                        companyID={this.state.customer.companyProfileID}
                        companyName={this.state.customer.customerName}
                        onCancel={this.closeGracePeriodModal}
                        onGracePeriodChange={this.onGracePeriodChange}
                        onSave={this.submitGracePeriod}
                        gracePeriod={this.state.gracePeriod ? moment(this.state.gracePeriod) : undefined}
                    />
                }
                {this.state.showConfirmationModal &&
                    <ConfirmationModal
                        message={this.state.confirmationParams.message}
                        onCancel={this.state.confirmationParams.onCancel}
                        onConfirm={this.state.confirmationParams.onConfirm}
                        title= {this.state.confirmationParams.title}
                    />
                }
                {!this.state.loading &&
                    <div>
                        {
                          this.state.errorMessage &&
                          <Alert className='space-between' message={this.state.errorMessage} type={'error'}/>
                        }
                        <div className='flex charge-history-header space-between'>
                            <h2>
                                <Icon type="credit-card"/> Charge History
                                for {`${this.state.customer.customerName}`}
                                <small className="payment-processor"><br/>
                                    Payment Processor:&nbsp;
                                    <span
                                        className="bold">{`${this.state.processorName ||
                                    'Unknown'}`}</span>
                                </small>
                            </h2>
                            {moreOptions}
                        </div>
                        {!this.state.customer.isResellerClient &&
                        <div className='plan-container flex space-between'>
                            {this.renderPlanDetails()}
                            <p className='plan-type'>{this.state.subscription.planType}</p>
                        </div>
                        }
                        <div className='charge-history-meta flex space-between'>
                            <p>
                                Current Credit Balance: <span>{this.state.creditBalance} / {this.state.creditAmount}</span>

                            </p>
                            {this.state.gracePeriod && <p>Grace Period Expires: <span>{moment(this.state.gracePeriod).format('MM/DD/YYYY')}</span></p>}
                            {!this.state.customer.isResellerClient &&
                            <p>Next Billing Date: <span>{moment(this.state.subscription.nextChargeDate).format('MMMM D, YYYY')}</span></p>
                            }
                        </div>
                    </div>
                }
                <div className="flex">
                    <div id="charge-history-table">
                        <Table
                            bordered={false}
                            columns={invoiceColumns}
                            dataSource={invoiceTableData}
                            expandedRowKeys={this.state.expandedRowKeys}
                            expandedRowRender={this.renderInvoiceDetail}
                            expandRowByClick={true}
                            loading={this.state.loading}
                            onExpand={this.changeSelectedInvoice}
                            pagination={false}
                            rowClassName={this.stripeTableClass}
                            size="small"
                        />
                    </div>
                    <div id="usageDetailContainer">
                        {!this.state.loading && !!this.getActiveUsageDetails() &&
                            <UsageDetails
                                date={this.getUsageDate()}
                                details={this.getActiveUsageDetails()}
                            />
                        }
                        {!this.state.loading && !this.getActiveUsageDetails() &&
                            this.getNoUsageDetailsText()
                        }
                    </div>
                </div>
            </div>
        );
    }

    private renderInvoiceDetail = (invoiceRow: IInvoiceTable): JSX.Element => {
        const isReseller = this.state.subscription.planType === "Marketing Automation Reseller";
        const childCompany = { dataIndex: 'childCompany', title: 'Child Company', width: '30%' };
        const invoiceDetailColumns: Array<ColumnProps<IInvoiceDetailTable>> = [
            {
                dataIndex: 'description',
                title: 'Description',
                width: '40%',
            },
            {
                dataIndex: 'units',
                title: 'Units',
                width: '10%',
            },
            {
                dataIndex: 'amount',
                title: 'Amount',
                width: '20%',
            },
        ];

        if (isReseller) {
            invoiceDetailColumns.unshift(childCompany);
        }

        const rawInvoiceData: IInvoice | undefined = this.state.invoices.find((invoice: IInvoice) => invoiceRow.key === invoice.id);

        if (rawInvoiceData === undefined) {
            return <div><p>Error Loading invoice details, please try again. Inform Team Chimera if this error persists.</p></div>;
        }

        const invoiceDetailTableData: IInvoiceDetailTable[] = rawInvoiceData.lineItems !== null ? rawInvoiceData.lineItems.map((lineItem: ILineItem) => {
            const companyName = lineItem.childCompanyID ?
            `${lineItem.childCompanyName} (${lineItem.childCompanyID})` :
            '';

            return {
                amount: lineItem.formattedAmount,
                childCompany: companyName,
                description: lineItem.description,
                key: parseInt('' + lineItem.id + rawInvoiceData.id, 10),
                type: lineItem.type || '',
                units: lineItem.units
            };
        }) : [];

        return (
            <Table
                bordered={false}
                columns={invoiceDetailColumns}
                dataSource={invoiceDetailTableData}
                pagination={false}
                rowClassName={this.stripeTableClass}
                size='small'
            />
        );
    };

    private renderPlanDetails = (): JSX.Element => {
        if (this.state.subscription.planType === "Marketing Automation Reseller") {
            return (
                <div className='flex space-between'>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.planPrice}`}</span>
                        <span className='plan-stat-detail'>plan<br />price</span>
                    </div>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.maxClients}`}</span>
                        <span className='plan-stat-detail'>clients per<br />price</span>
                    </div>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.maxEmails}`}</span>
                        <span className='plan-stat-detail'>emails per<br />instance</span>
                    </div>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.activeClients}`}</span>
                        <span className='plan-stat-detail'>active<br />clients</span>
                    </div>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.emailsUsed}`}</span>
                        <span className='plan-stat-detail'>emails<br />used</span>
                    </div>
                </div>
            );
        } else {
            return (
                <div className='flex space-between'>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.planPrice}`}</span>
                        <span className='plan-stat-detail'>monthly<br />price</span>
                    </div>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.leads}`}</span>
                        <span className='plan-stat-detail'>number<br />contacts</span>
                    </div>
                    <div className='flex plan-stat-container'>
                        <span className='plan-stat'>{`${this.state.subscription.emailsUsed}`}</span>
                        <span className='plan-stat-detail'>emails<br />used</span>
                    </div>
                </div>
            );
        }
    };

    private getActiveUsageDetails = (): IUsageDetail[] => {
        const currentSelection = this.state.currentSelectedInvoice;

        if (currentSelection !== undefined && currentSelection !== null) {
            const invoice = this.state.invoices.find(
                (invoiceElement: IInvoice) => invoiceElement.id === currentSelection.key
            );

            if (invoice !== undefined) {
                return invoice.usageDetails;
            }
        }

        return this.state.subscription.usageDetails;
    };

    private getUsageDate = (): string => {
        const currentSelection = this.state.currentSelectedInvoice;

        if ( currentSelection !== undefined && currentSelection !== null) {
            const invoice = this.state.invoices.find(
                (invoiceElement: IInvoice) => invoiceElement.id === currentSelection.key
            );

            if (invoice !== undefined) {
                return invoice.statementDate;
            }
        }

        return this.state.subscription.nextChargeDate;
    };

    private getNoUsageDetailsText = (): string => {
        const currentSelection = this.state.currentSelectedInvoice;
        const text: string = currentSelection !== undefined && currentSelection !== null ?
            `No Usage Details Available for Invoice ${currentSelection.key}.` : 'No Usage Details Available.';
        return text;
    };

    private changeSelectedInvoice = (expanded: boolean, invoiceRow: IInvoiceTable): void => {
        const keys = [];
        if (expanded) {
            keys.push(invoiceRow.key);
            this.setState({ currentSelectedInvoice: invoiceRow });
        } else {
            this.setState({ currentSelectedInvoice: undefined });
        }

        this.setState({ expandedRowKeys: keys });
    };

    private stripeTableClass = (record: any, index: any): string => index % 2 !== 0 ? 'row-stripe' : '';

    private openBillingDateModal = () => this.setState({ showBillingDateModal: true });

    private closeBillingDateModal = () => this.setState({ showBillingDateModal: false });

    private submitBillingDateModal = () => {
        this.setState({
            loading: true,
            showBillingDateModal: false
        });

        // Refetch the charge history so that the state recalculates with the new cycle day
        // We don't care about the promise from the async call, we just need to acknowledge it
        this.getChargeHistory().then();
    };

    private openCreditsModal = () => this.setState({ showCreditsModal: true });

    private closeCreditsModal = () => this.setState({ showCreditsModal: false });

    private openGracePeriodModal = () => this.setState({ showGracePeriodModal: true });

    private closeGracePeriodModal = () => this.setState({ showGracePeriodModal: false, gracePeriod: this.state.customer.gracePeriod });

    private openManualChargeModal = () => this.setState({ showManualChargeModal: true });

    private closeManualChargeModal = () => this.setState({ showManualChargeModal: false });

    private submitGracePeriod = async () => {
        this.setState({ loading: true });
        const response = await setGracePeriod(
            this.state.customer.id,
            this.state.gracePeriod ?? ''
        );

        if (!response.success) {
            this.setState({
                errorMessage: response.error.message,
                loading: false
            });
        } else {
            this.setState({
                customer : {
                    ...this.state.customer,
                    gracePeriod: this.state.gracePeriod,
                },
                errorMessage: null,
                loading: false,
                showGracePeriodModal: false,
            });
        }
    };

    private onGracePeriodChange = (date: moment.Moment, dateString: string) => this.setState({ gracePeriod: dateString});

    private submitManualChargeModal = () => {
        this.setState({
            loading: true,
            showManualChargeModal: false
        });

        // We don't care about the promise from the async call, we just need to acknowledge it
        this.getChargeHistory().then();
    };

    private openRefundModal = (invoiceID: number) => {
        this.setState({
            showRefundModal: true,
            targetInvoice: this.state.invoices.find(invoice => invoice.id === invoiceID),
        });
    };

    private closeRefundModal = () => this.setState({ showRefundModal: false });

    private openResetNotificationModal = () => this.setState(
        {
            confirmationParams: {
                message: 'This option removes any billing notifications that have been set for ' + this.state.customer.customerName + ' and removes any related restriction.\nAre you sure you want to proceed?',
                onCancel: this.closeConfirmationModal,
                onConfirm: this.submitResetNotificationModal,
                title: 'Reset Billing Notifications For ' + this.state.customer.customerName
            },
            showConfirmationModal : true
        }
    );

    private closeConfirmationModal = () => this.setState({ confirmationParams: {}, showConfirmationModal : false });

    private submitResetNotificationModal = async () => {
        this.setState({
            loading: true,
            showConfirmationModal : false
        });

        const response = await resetBillingNotifications(this.props.companyID);

        if (!response.success) {
            this.setState({
                errorMessage: response.error.message,
                loading: false
            });
        } else {
            this.setState({
                errorMessage: null,
                loading: false,
                showConfirmationModal : false
            });
        }
    };

    private openChangePaymentMethodModal = () => this.setState({ showChangePaymentMethodModal: true });
    private closeChangePaymentMethodModal = () => this.setState({ showChangePaymentMethodModal: false });

    private openSetStripeCustomerModal = () => this.setState({ showSetStripeCustomerModal: true });
    private closeSetStripeCustomerModal = () => this.setState({ showSetStripeCustomerModal: false });

    private openRetryModal = (invoiceID: number) => () => this.setState(
        {
            confirmationParams: {
                message: 'This will immediately try to charge the customer for this payment.\nAre you sure you want to proceed?',
                onCancel: this.closeConfirmationModal,
                onConfirm: this.retryTransaction(invoiceID),
                title: 'Retry Charge Confirmation'
            },
            showConfirmationModal : true
        }
    );

    private retryTransaction = (invoiceID: number) => async () => {
        this.setState({
            confirmationParams: {},
            loading: true,
            showConfirmationModal : false
        });

        const response = await retryTransaction(invoiceID);

        if (response.status !== 'success') {
            this.setState({
                errorMessage: response.message,
                loading: false
            });
        } else {
            // Don't unset the loading state, since we need to reload the history anyway
            this.setState({
                errorMessage: null,
                showConfirmationModal : false
            });

            this.getChargeHistory().then();
        }
    };

    private openVoidModal = (invoiceID: number) => () => this.setState(
        {
            confirmationParams: {
                message: 'This will set the invoice and payment to be voided internally and in Stripe. Charges in external systems other than Stripe will NOT be automatically voided and should be addressed manually.\nAre you sure you want to proceed?',
                onCancel: this.closeConfirmationModal,
                onConfirm: this.voidTransaction(invoiceID),
                title: 'Void Charge Confirmation'
            },
            showConfirmationModal : true
        }
    );

    private voidTransaction = (invoiceID: number) => async () => {
        this.setState({
            confirmationParams: {},
            loading: true,
            showConfirmationModal : false
        });

        const response = await voidTransaction(invoiceID);

        if (!response.success) {
            this.setState({
                errorMessage: response.errors.message,
                loading: false
            });
        } else {
            // Don't unset the loading state, since we need to reload the history anyway
            this.setState({
                errorMessage: null,
                showConfirmationModal : false
            });

            this.getChargeHistory().then();
        }
    };

    private submitChangePaymentMethodModal = () => {
        this.setState({
            errorMessage: 'The processor for this customer has changed. Please inform them to update their billing information.',
            loading: true,
            showChangePaymentMethodModal: false
        });

        // We don't care about the promise from the async call, we just need to acknowledge it
        this.getChargeHistory().then();
    };

    private submitSetStripeCustomerModal = () => {
        this.setState({
            errorMessage: 'The Stripe customer for this company has been set.',
            loading: true,
            showSetStripeCustomerModal: false
        });

        this.getChargeHistory().then();
    };

    private async getChargeHistory() {
        const chargeHistory = await getChargeHistory(this.props.companyID, this.props.token);
        if (!chargeHistory.success) {
            this.setState({
                errorMessage: chargeHistory.error.message,
                loading: false,
            });
        } else {
            this.setState({
                creditAmount: chargeHistory.creditAmount,
                creditBalance: chargeHistory.creditBalance,
                currency: chargeHistory.currency,
                customer: chargeHistory.customer,
                gracePeriod: chargeHistory.customer.gracePeriod,
                invoices: chargeHistory.invoices,
                loading: false,
                processorName: chargeHistory.processorName,
                subscription: chargeHistory.subscriptionDetails,
            });
        }
    }
}

export default ChargeHistory;
